/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { Role } from '../../shared/entities/role';
import ActionCreator from '../action-creator';

interface UpdateRoleProps {
  id: string | null;
  roleData: Role;
}

export const ActionCreators = {
  // For requesting list of roles
  RequestRoles: new ActionCreator<'RequestRoles', any>('RequestRoles'),
  ReceivedRolesSuccess: new ActionCreator<'ReceivedRolesSuccess', any>('ReceivedRolesSuccess'),
  ReceivedRolesError: new ActionCreator<'ReceivedRolesError', any>('ReceivedRolesError'),
  ClearReceivedRolesError: new ActionCreator<'ClearReceivedRolesError', any>('ClearReceivedRolesError'),

  // For requesting a specific role
  RequestCurrentRole: new ActionCreator<'RequestCurrentRole', string>('RequestCurrentRole'),
  ReceivedCurrentRoleSuccess: new ActionCreator<'ReceivedCurrentRoleSuccess', any>('ReceivedCurrentRoleSuccess'),
  ReceivedCurrentRoleError: new ActionCreator<'ReceivedCurrentRoleError', any>('ReceivedCurrentRoleError'),
  ClearReceivedCurrentRoleError: new ActionCreator<'ClearReceivedCurrentRoleError', any>(
    'ClearReceivedCurrentRoleError',
  ),
  ClearCurrentRole: new ActionCreator<'ClearCurrentRole', any>('ClearCurrentRole'),

  // For creating a role
  ProvisionRole: new ActionCreator<'ProvisionRole', Role>('ProvisionRole'),
  RoleProvisioningSuccess: new ActionCreator<'RoleProvisioningSuccess', any>('RoleProvisioningSuccess'),
  RoleProvisioningError: new ActionCreator<'RoleProvisioningError', any>('RoleProvisioningError'),
  ClearRoleProvisioningError: new ActionCreator<'ClearRoleProvisioningError', any>('ClearRoleProvisioningError'),

  // For deleting a role
  DeleteRole: new ActionCreator<'DeleteRole', string>('DeleteRole'),
  DeleteRoleSuccess: new ActionCreator<'DeleteRoleSuccess', any>('DeleteRoleSuccess'),
  DeleteRoleError: new ActionCreator<'DeleteRoleError', any>('DeleteRoleError'),
  ClearDeleteRoleError: new ActionCreator<'ClearDeleteRoleError', any>('ClearDeleteRoleError'),

  // For updating a role
  UpdateRole: new ActionCreator<'UpdateRole', UpdateRoleProps>('UpdateRole'),
  UpdateRoleSuccess: new ActionCreator<'UpdateRoleSuccess', any>('UpdateRoleSuccess'),
  UpdateRoleError: new ActionCreator<'UpdateRoleError', any>('UpdateRoleError'),
  ClearUpdateRoleError: new ActionCreator<'ClearUpdateRoleError', any>('ClearUpdateRoleError'),
};

export type Action = typeof ActionCreators[keyof typeof ActionCreators];

export function requestRoles() {
  return ActionCreators.RequestRoles.create();
}
export function clearReceivedRolesError() {
  return ActionCreators.ClearReceivedRolesError.create();
}

export function requestCurrentRole(id: string) {
  return ActionCreators.RequestCurrentRole.create(id);
}
export function clearReceivedCurrentRoleError() {
  return ActionCreators.ClearReceivedCurrentRoleError.create();
}
export function clearCurrentRole() {
  return ActionCreators.ClearCurrentRole.create();
}

export function provisionRole(roleData: Role) {
  return ActionCreators.ProvisionRole.create(roleData);
}
export function clearRoleProvisioningError() {
  return ActionCreators.ClearRoleProvisioningError.create();
}

export function deleteRole(id: string) {
  return ActionCreators.DeleteRole.create(id);
}
export function clearDeleteRoleError() {
  return ActionCreators.ClearDeleteRoleError.create();
}

export function updateRole(id: string, roleData: Role) {
  return ActionCreators.UpdateRole.create({
    id: id,
    roleData: roleData,
  });
}
export function clearUpdateRoleError() {
  return ActionCreators.ClearUpdateRoleError.create();
}
